import { useEffect } from 'react';
import { fireEvent } from '@stitch-fix/kufak-gtm-client';
import type { ConfigParam } from '../types/definitions/vendor/graphql-schema';
import { useAllocation } from '../hooks/useAllocation';
import { addFeatureFlagsToRumContext, browserLogger } from '../utils/loggers/browser';
import { isAndroidApp } from '../utils/registration/android';
export const SSO_OPTIMIZATION_CONFIG_PARAM_NAME = 'eng.onboarding.sso_optimization_v2' as const;
export const SSO_OPTIMIZATION_CONTROL = 'control' as const;
export const SSO_OPTIMIZATION_SSO_FIRST = 'sso_first' as const;
const SSO_OPTIMIZATION_EXPECTED_VALUES = [SSO_OPTIMIZATION_CONTROL, SSO_OPTIMIZATION_SSO_FIRST] as const;
type SsoOptimizationAllocationValue = (typeof SSO_OPTIMIZATION_EXPECTED_VALUES)[number];
const isSsoOptimizationAllocationValue = (allocationValue: unknown): allocationValue is SsoOptimizationAllocationValue => typeof allocationValue === 'string' && SSO_OPTIMIZATION_EXPECTED_VALUES.includes(allocationValue as SsoOptimizationAllocationValue);
const getSsoOptimizationAllocationValue = (configParams?: ConfigParam[]): SsoOptimizationAllocationValue | undefined => {
  const allocationValue = configParams?.find(configParam => configParam.name === SSO_OPTIMIZATION_CONFIG_PARAM_NAME)?.value;
  return isSsoOptimizationAllocationValue(allocationValue) ? allocationValue : undefined;
};

/**
 * SSO Optimization experiment hook that returns the allocation value for the current visitor.
 */
export const useSsoOptimizationAllocation = (options: {
  skip?: boolean;
} = {}) => {
  const shouldSkipAllocation = options.skip || isAndroidApp();
  const {
    data,
    loading,
    error
  } = useAllocation([{
    expectedValues: [...SSO_OPTIMIZATION_EXPECTED_VALUES],
    fallbackValue: SSO_OPTIMIZATION_CONTROL,
    name: SSO_OPTIMIZATION_CONFIG_PARAM_NAME
  }], {
    skip: shouldSkipAllocation
  });
  const ssoOptimizationAllocation = shouldSkipAllocation ? SSO_OPTIMIZATION_CONTROL : getSsoOptimizationAllocationValue(data?.visitor?.configParams);
  useEffect(() => {
    if (!error || shouldSkipAllocation) return;
    browserLogger({
      level: 'error',
      team: 'client-onboarding',
      feature: 'registration',
      message: '[SSO Optimization] Failed to allocate visitor to experiment',
      error
    });
  }, [error, shouldSkipAllocation]);
  useEffect(() => {
    if (loading || error || shouldSkipAllocation || !ssoOptimizationAllocation) return;

    // Fire a GTM event to track the allocation value in FullStory
    fireEvent({
      eventName: 'expt_1799',
      // Fab experiment id
      eventAttributes: {
        allocationValue: ssoOptimizationAllocation
      }
    });

    // Add feature flag to RUM context
    addFeatureFlagsToRumContext({
      [SSO_OPTIMIZATION_CONFIG_PARAM_NAME]: ssoOptimizationAllocation
    });
  }, [error, loading, shouldSkipAllocation, ssoOptimizationAllocation]);
  return {
    ssoOptimizationAllocation: ssoOptimizationAllocation ?? SSO_OPTIMIZATION_CONTROL,
    allocationLoading: loading
  };
};